const DEFAULT_TIMEZONE = "Asia/Ashgabat";
/**
 * App locale codes (`en` / `ru` / `tk`) → BCP-47 tags Intl understands.
 * Anything already in BCP-47 form (e.g. `ru-RU`) passes through unchanged.
 */
const LOCALE_MAP = {
    en: "en-GB",
    ru: "ru-RU",
    tk: "tk-TM",
};
const DEFAULT_LOCALE = "en";
function resolveLocale(locale) {
    return LOCALE_MAP[locale] ?? locale;
}
function toDate(isoDate) {
    if (!isoDate)
        return null;
    const date = isoDate instanceof Date ? isoDate : new Date(isoDate);
    return Number.isNaN(date.getTime()) ? null : date;
}
/**
 * Canonical short date — `16 June 2026` (day, full month name, year) in the
 * Asia/Ashgabat timezone. The month name follows the UI `locale` you pass
 * (`en` → June, `ru` → июня, `tk` → iýun). Returns an em-dash for
 * nullish/invalid input so it's safe to drop straight into UI strings.
 *
 * This is the single source of truth for date formatting platform-wide.
 * `@rr/ui` re-exports it so frontend code can `import { formatDate } from '@rr/ui'`.
 */
export function formatDate(isoDate, locale = DEFAULT_LOCALE, timeZone = DEFAULT_TIMEZONE) {
    const date = toDate(isoDate);
    if (!date)
        return "—";
    return new Intl.DateTimeFormat(resolveLocale(locale), {
        timeZone,
        day: "numeric",
        month: "long",
        year: "numeric",
    }).format(date);
}
/**
 * Canonical date + time — `16 June 2026, 14:30` (24h) in Asia/Ashgabat.
 * Month name follows the UI `locale`, same as {@link formatDate}.
 */
export function formatDateTime(isoDate, locale = DEFAULT_LOCALE, timeZone = DEFAULT_TIMEZONE) {
    const date = toDate(isoDate);
    if (!date)
        return "—";
    return new Intl.DateTimeFormat(resolveLocale(locale), {
        timeZone,
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).format(date);
}
/**
 * Relative time for feeds / notifications — `just now`, `5m ago`, `3h ago`,
 * `2d ago`. Falls back to {@link formatDate} once the gap exceeds a week so
 * old items still read as an absolute date. Returns an em-dash for invalid input.
 */
export function formatRelative(isoDate, locale = DEFAULT_LOCALE, timeZone = DEFAULT_TIMEZONE) {
    const date = toDate(isoDate);
    if (!date)
        return "—";
    const diff = Date.now() - date.getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1)
        return "just now";
    if (mins < 60)
        return `${mins}m ago`;
    if (hours < 24)
        return `${hours}h ago`;
    if (days < 7)
        return `${days}d ago`;
    return formatDate(date, locale, timeZone);
}
/** Parse a calendar date locally; datetime strings keep their explicit offset. */
export function parseDateInput(value) {
    if (!value)
        return null;
    const trimmed = value.trim();
    const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
    if (!parts) {
        const date = new Date(trimmed);
        return Number.isNaN(date.getTime()) ? null : date;
    }
    const [, year, month, day] = parts.map(Number);
    const date = new Date(0);
    date.setFullYear(year, month - 1, day);
    date.setHours(0, 0, 0, 0);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
        ? date
        : null;
}
/** True when `YYYY-MM-DD` names a real calendar day (rejects 2026-02-30, 2026-13-01). */
function isCalendarDate(ymd) {
    const [year, month, day] = ymd.split("-").map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}
const ISO_DATETIME = /^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;
/**
 * Split an ISO-shaped `YYYY-MM-DD[ |T]HH:MM[:SS[.fff]][Z|±HH:MM]` string.
 * Returns null when it starts like an ISO date but a field is impossible
 * (2026-02-30, 25:00, a trailing typo), undefined when it isn't ISO-shaped.
 */
function parseIsoParts(value) {
    if (!/^\d{4}-\d{2}-\d{2}/.test(value))
        return undefined;
    const m = ISO_DATETIME.exec(value);
    if (!m || !isCalendarDate(m[1]))
        return null;
    const [, date, hh, mm, ss, offset] = m;
    if (hh !== undefined && (Number(hh) > 23 || Number(mm) > 59 || Number(ss ?? 0) > 59))
        return null;
    return { date, hasTime: hh !== undefined, offset };
}
/**
 * Normalize any date/datetime string to `YYYY-MM-DD` (returns null if invalid).
 * Bare dates and offset-less wall-clock times keep their own calendar day; an
 * instant (`Z` / `+05:00`) is read in Asia/Ashgabat, the same zone
 * {@link formatDate} displays in, so the input and the screen agree on the day
 * whatever the runtime zone is. Parsing helper, not display formatting.
 */
export function toDateOnly(value, timeZone = DEFAULT_TIMEZONE) {
    if (!value)
        return null;
    const trimmed = value.trim();
    const iso = parseIsoParts(trimmed);
    if (iso === null)
        return null;
    if (iso && !iso.offset)
        return iso.date;
    const date = new Date(iso ? trimmed.replace(" ", "T") : trimmed);
    if (Number.isNaN(date.getTime()))
        return null;
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(date);
    const part = (type) => parts.find((p) => p.type === type)?.value;
    return `${part("year")}-${part("month")}-${part("day")}`;
}
/** Safe value for `<input type="date">` — `YYYY-MM-DD` or empty string. */
export function getDateInputValue(value) {
    return toDateOnly(value) ?? "";
}
/**
 * Convert a date string (`YYYY-MM-DD`) to an ISO datetime string
 * (`YYYY-MM-DDTHH:MM:SSZ`). Defaults to `00:00:00Z` if no time is specified.
 * Returns null if invalid.
 */
export function toISODateTime(dateStr) {
    if (!dateStr)
        return null;
    const trimmed = dateStr.trim();
    if (parseIsoParts(trimmed) === null)
        return null;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(trimmed))
        return trimmed;
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed))
        return `${trimmed}T00:00:00Z`;
    const date = new Date(trimmed);
    if (Number.isNaN(date.getTime()))
        return null;
    return date.toISOString();
}
export { DEFAULT_TIMEZONE };
//# sourceMappingURL=date.js.map